import React, { SyntheticEvent, useContext, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  ListItem,
  Paper,
  Snackbar,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";

import { ChargifyState } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_provider_pb";
import {
  CreditCardDetails,
  PayPalAccountDetails,
  PaymentCollectionMethod,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/payment_method_pb";
import { SubscriptionState } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import {
  BillingDetails,
  PaymentMethod,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import {
  Role,
  SubscriptionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import SubscriptionAction from "./subscription-action";
import { deletePaymentMethods } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { FEATURE_FLAG_CREDIT_CARD_LESS_EXPERIENCE } from "constants/billing-experiences";
import {
  DEFAULT_SNACKBAR_DURATION,
  EventTypeReason,
  NO_DISPLAY_DATA,
} from "constants/index";
import { paymentMethodTypeToString } from "constants/payment-method";
import { UseGetFeatureFlagValue } from "context/feature-flags-context";
import { OrgDataContext, SubDataContext } from "context/organisation-context";
import { PaymentMethodsContext } from "context/payment-methods-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import { toUUID } from "helpers/s12-helpers";
import { UrlParamTypes } from "types/org-display";

const RemovePaymentMethod = ({
  refreshOrgData,
}: {
  refreshOrgData: () => void;
}) => {
  const theme = useTheme();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

  const { orgData, setOrgData } = useContext(OrgDataContext);
  const { subData } = useContext(SubDataContext);
  const { defaultPaymentMethod, defaultPaymentMethodDetails } = useContext(
    PaymentMethodsContext
  );
  const { showError } = useContext(SnackbarContext);
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);

  const subscription = orgData.subscription[0];
  const isZuoraOrg = subscription?.billingProvider === "ZUORA";
  const zuoraSubscription = subData?.subscription;
  const hasCCLExp = UseGetFeatureFlagValue(
    FEATURE_FLAG_CREDIT_CARD_LESS_EXPERIENCE
  );
  const isAutomaticCollection = isZuoraOrg
    ? zuoraSubscription?.paymentCollectionMethod ===
      PaymentCollectionMethod.AUTOMATIC
    : subscription?.chargify?.paymentMethod === PaymentMethod.AUTOMATIC;
  const isTrialing = isZuoraOrg
    ? zuoraSubscription?.state === SubscriptionState.TRIALING
    : subscription?.chargify?.state === ChargifyState.TRIALING;

  const hasPaymentMethod = defaultPaymentMethod !== undefined;

  const isFreeOrg =
    subscription &&
    (subscription.type === SubscriptionType.FREE ||
      subscription.type === SubscriptionType.FREE_ORG);

  const shouldCancelAtEndOfPeriod =
    hasCCLExp &&
    !isFreeOrg &&
    isTrialing &&
    isAutomaticCollection &&
    hasPaymentMethod;

  const displayPaymentMethodName = () => {
    switch (defaultPaymentMethodDetails?.getType()) {
      case CreditCardDetails: {
        const details = defaultPaymentMethodDetails as CreditCardDetails;
        return details.firstName + " " + details.lastName;
      }
      case PayPalAccountDetails: {
        const details = defaultPaymentMethodDetails as PayPalAccountDetails;
        return details.firstName + " " + details.lastName;
      }
      default:
        return NO_DISPLAY_DATA;
    }
  };

  const handleDeletePaymentMethodStateUpdate = () => {
    if (isZuoraOrg) {
      return;
    }
    const newOrgData = { ...orgData } as Role;
    if (newOrgData.subscription[0].chargify?.billingDetails) {
      newOrgData.subscription[0].chargify.billingDetails = new BillingDetails();
      setOrgData(newOrgData);
    }
  };

  const handleDeletePaymentMethod = () => {
    if (!defaultPaymentMethod) {
      return;
    }

    return deletePaymentMethods(
      {
        organizationId: toUUID(roleId),
        ids: [defaultPaymentMethod.id],
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then(() => {
        handleDeletePaymentMethodStateUpdate();
        if (!shouldCancelAtEndOfPeriod) {
          setShowLoadingSpinner(false);
        }
      })
      .then(() => {
        refreshOrgData();
      })
      .catch(showError);
  };

  const handleClose = (
    event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  const handleSubmit = async () => {
    setShowLoadingSpinner(true);
    setIsSubscriptionActionLoading(true);
    await handleDeletePaymentMethod();
    setOpenSuccessAlert(true);
    setShowLoadingSpinner(false);
    setIsSubscriptionActionLoading(false);
  };

  const warnContainerSx = {
    background: "#ffd4d4",
    border: "1px solid #941c1c",
    color: "#941c1c",
    borderRadius: 1,
    padding: theme.spacing(1.25),
    margin: "6px 4px 0px",
  };

  const displayCancelTrialEndOfPeriodMessage = () => {
    return (
      <Grid item xs={12} data-testid={"cancel-at-end-of-period-status-message"}>
        <Box sx={warnContainerSx}>
          <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
            This organization is on the credit card less (CCL) trial.
          </ListItem>
          <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
            Removing the payment method attached will set the premium plan to
            cancel at the end of the trial period.
          </ListItem>
        </Box>
      </Grid>
    );
  };

  const displayTabContent = () => {
    if (isFreeOrg) {
      return NO_DISPLAY_DATA;
    }

    switch (defaultPaymentMethodDetails?.getType()) {
      case CreditCardDetails: {
        const details = defaultPaymentMethodDetails as CreditCardDetails;
        return `XXXX-XXXX-XXXX-${details.lastFour}`;
      }
      case PayPalAccountDetails:
        const details = defaultPaymentMethodDetails as PayPalAccountDetails;
        return details.email;
      default:
        return NO_DISPLAY_DATA;
    }
  };

  const displayConfirmCardDetails = () => {
    if (defaultPaymentMethodDetails?.getType() !== CreditCardDetails) {
      return <></>;
    }

    const card = defaultPaymentMethodDetails as CreditCardDetails;
    const cardDisplay = [
      [["Cardholder name"], [`${card.firstName} ${card.lastName}`]],
      [["Type"], [card.cardType]],
      [["Last four"], [card.lastFour]],
      [
        ["Expiration (MM/YYYY)"],
        [`${card.expirationMonth}/${card.expirationYear}`],
      ],
    ];

    return (
      <>
        <TableRow>
          <TableCell colSpan={2} align="center">
            <Typography
              variant="body1"
              data-testid="confirm-credit-card-details"
            >
              <b>Details</b>
            </Typography>
          </TableCell>
        </TableRow>
        {cardDisplay.map((row) => {
          return (
            <>
              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    <b>{row[0]}:</b>
                  </Typography>
                </TableCell>
                <TableCell align="left">
                  <Typography variant="body2">{row[1]}</Typography>
                </TableCell>
              </TableRow>
            </>
          );
        })}
      </>
    );
  };

  const displayConfirmPayPalDetails = () => {
    if (defaultPaymentMethodDetails?.getType() !== PayPalAccountDetails) {
      return <></>;
    }

    const paypal = defaultPaymentMethodDetails as PayPalAccountDetails;
    const paypalDisplay = [
      [["Account owner"], [`${paypal.firstName} ${paypal.lastName}`]],
      [["Email"], [paypal.email]],
    ];

    return (
      <>
        <TableRow>
          <TableCell colSpan={2} align="center">
            <Typography variant="body1" data-testid="confirm-paypal-details">
              <b>Details</b>
            </Typography>
          </TableCell>
        </TableRow>
        {paypalDisplay.map((row) => {
          return (
            <>
              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    <b>{row[0]}:</b>
                  </Typography>
                </TableCell>
                <TableCell align="left">
                  <Typography variant="body2">{row[1]}</Typography>
                </TableCell>
              </TableRow>
            </>
          );
        })}
      </>
    );
  };

  const displayConfirmationMessage = () => {
    let display: React.ReactElement = <></>;
    switch (defaultPaymentMethodDetails?.getType()) {
      case CreditCardDetails: {
        display = displayConfirmCardDetails();
        break;
      }
      case PayPalAccountDetails: {
        display = displayConfirmPayPalDetails();
        break;
      }
      default:
        display = <></>;
    }

    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        display={"flex"}
        flexDirection={"row"}
        xs={12}
      >
        <Grid item xs={2}></Grid>
        <Grid item xs={6}>
          <TableContainer component={Paper}>
            <Table size="small">{display}</Table>
          </TableContainer>
        </Grid>
        <Grid item xs={2}></Grid>
      </Grid>
    );
  };

  return (
    <React.Fragment>
      <SubscriptionAction
        title={`Payment Method (${
          defaultPaymentMethod
            ? paymentMethodTypeToString[defaultPaymentMethod.paymentMethodType]
            : NO_DISPLAY_DATA
        }):`}
        displayInfo={displayPaymentMethodName()}
        middleElem={
          <React.Fragment>
            <Typography
              sx={{
                mt: 1,
                fontSize: theme.typography.body2.fontSize,
                fontWeight: theme.typography.body2.fontWeight,
              }}
              data-anchor="payment-method-middle-elem"
            >
              {displayTabContent()}
            </Typography>
          </React.Fragment>
        }
      >
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="secondary"
            fullWidth
            disabled={
              !defaultPaymentMethod || isFreeOrg || isSubscriptionActionLoading
            }
            onClick={() => setOpenConfirmDialog(true)}
            data-testid="remove-payment-method-btn"
          >
            REMOVE PAYMENT METHOD
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Remove Payment Method"
        open={openConfirmDialog}
        width={"md"}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        setOpen={setOpenConfirmDialog}
        onConfirm={() => handleSubmit()}
      >
        <Grid container spacing={3}>
          <Grid item spacing={1}>
            {shouldCancelAtEndOfPeriod
              ? displayCancelTrialEndOfPeriodMessage()
              : null}
          </Grid>
          <Grid item xs={12}>
            <Typography data-testid="remove-payment-method-dialog-text">
              Are you sure you want to <b>remove</b> the attached payment
              method?
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {displayConfirmationMessage()}
          </Grid>
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleClose}
      >
        <Alert severity="success" variant="filled">
          Payment method removed
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default RemovePaymentMethod;
